// ─── Update Chase — the editable subset of server models/chase-model.ts ───────
// Quotes are NOT edited here: this form only writes columns that live on the
// `chases` row itself — documents are only uploaded when renewing. Server-owned
// columns (userId, createdFrom, status, archived, callId, agentCode) and `mute`
// (its own row toggle, which writes a log) are deliberately left out.

import moment from "moment";
import { STATUS } from "./chase-actions.constants";
import {
  BRAND_OPTIONS,
  COVER_TYPES,
  type IBrandType,
  type ICoverType,
  type IPaymentType,
  type IPolicySource,
  type IPolicyType,
} from "./create-chase.constants";

export {
  BRAND_OPTIONS,
  POLICY_SOURCE_OPTIONS,
  COVER_TYPE_OPTIONS,
  LAPSE_REASONS,
  toNum,
} from "./create-chase.constants";

export type EditChaseForm = {
  forename: string;
  surname: string;
  companyName: string;
  email: string;
  telephone: string;
  dateOfBirth: string;
  postcode: string;
  address: string;
  policyReferenceNumber: string;
  /** Only a renewed chase may change its policy ref — never sent otherwise. */
  renewed: boolean;
  /** What tells two chases on the same policy reference apart. */
  versionReferenceNumber: string;
  brandCode: IBrandType;
  /** "" on chases created before the field existed — never defaulted for them. */
  policySource: IPolicySource | "";
  coverType: ICoverType;
  policyNumber: string;
  insurer: string;
  registrationNumber: string;
  renewalDate: string;
  startDate: string;
  policyType: IPolicyType;
  paymentType: IPaymentType;
  auto: boolean;
  lapseReason: string;
  premium: string;
  totalPayable: string;
  monthlyAmount: string;
  instalments: string;
  interestRate: string;
};

const text = (value: any): string => (value ?? "") as string;
/** Date columns arrive as ISO or "YYYY-MM-DD"; the date input needs the latter. */
const day = (value: any): string =>
  value && moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : "";
/** Money/count columns come back from MSSQL as strings — keep them as text. */
const amount = (value: any): string =>
  value === null || value === undefined || value === "" ? "" : String(value);

/** Seed the form from the chase row. */
export const toEditForm = (data: Record<string, any> = {}): EditChaseForm => ({
  forename: text(data.forename),
  surname: text(data.surname),
  companyName: text(data.companyName),
  email: text(data.email),
  telephone: text(data.telephone),
  dateOfBirth: day(data.dateOfBirth),
  postcode: text(data.postcode),
  address: text(data.address),
  policyReferenceNumber: text(data.policyReferenceNumber),
  renewed: data.status === STATUS.RENEWED,
  versionReferenceNumber: text(data.versionReferenceNumber),
  brandCode: data.brandCode ?? BRAND_OPTIONS[0].value,
  policySource: data.policySource ?? "",
  coverType: data.coverType ?? COVER_TYPES[0],
  policyNumber: text(data.policyNumber),
  insurer: text(data.insurer),
  registrationNumber: text(data.registrationNumber),
  renewalDate: day(data.renewalDate),
  startDate: day(data.startDate),
  policyType: data.policyType ?? "Standard",
  paymentType: data.paymentType ?? "cash",
  auto: !!data.auto,
  lapseReason: text(data.lapseReason),
  premium: amount(data.premium),
  totalPayable: amount(data.totalPayable),
  monthlyAmount: amount(data.monthlyAmount),
  instalments: amount(data.instalments),
  interestRate: amount(data.interestRate),
});

/** First problem with the form, or null when it's good to send. */
export const validateEditForm = (v: EditChaseForm): string | null => {
  const required: [keyof EditChaseForm, string][] = [
    ["forename", "Fore name"],
    ["surname", "Surname"],
    ["email", "Email"],
    ["telephone", "Phone"],
    ["renewalDate", "Renewal date"],
  ];
  if (v.renewed)
    required.push(["policyReferenceNumber", "Policy reference number"]);
  const missing = required.find(([key]) => !String(v[key]).trim());
  if (missing) return `${missing[1]} is required.`;
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.email.trim()))
    return "That email address doesn't look right.";
  if (v.paymentType === "instalments" && !Number(v.instalments))
    return "Instalment policies need a number of instalments (e.g. 10).";
  return null;
};

const trimmed = (value: string) => value.trim() || null;
const numeric = (value: string) => (value.trim() === "" ? null : Number(value));

/** The PATCH /chase/:id body — blanks become null, dates go out as ISO. */
export const toChasePayload = (v: EditChaseForm) => {
  const monthly = v.paymentType === "instalments";
  return {
    forename: v.forename.trim(),
    surname: v.surname.trim(),
    companyName: trimmed(v.companyName),
    email: v.email.trim(),
    telephone: v.telephone.trim(),
    dateOfBirth: trimmed(v.dateOfBirth),
    postcode: trimmed(v.postcode)?.toUpperCase() ?? null,
    address: trimmed(v.address),
    // Version is read-only here — ⋮ "Update Version Reference" moves the docs.
    ...(v.renewed
      ? { policyReferenceNumber: v.policyReferenceNumber.trim() }
      : {}),
    brandCode: v.brandCode,
    policySource: v.policySource || null,
    coverType: v.coverType,
    policyNumber: trimmed(v.policyNumber),
    insurer: trimmed(v.insurer),
    registrationNumber: trimmed(v.registrationNumber),
    renewalDate: new Date(v.renewalDate).toISOString(),
    ...(v.startDate ? { startDate: new Date(v.startDate).toISOString() } : {}),
    policyType: v.policyType,
    paymentType: v.paymentType,
    auto: v.auto,
    // Auto-renewing policies aren't lapsing, so they carry no reason.
    lapseReason: v.auto ? null : trimmed(v.lapseReason),
    premium: numeric(v.premium),
    // Instalment-only figures; a cash policy stores none of them.
    instalments: monthly ? numeric(v.instalments) : null,
    interestRate: monthly ? numeric(v.interestRate) : null,
    monthlyAmount: monthly ? numeric(v.monthlyAmount) : null,
    totalPayable: monthly ? numeric(v.totalPayable) : null,
  };
};
